// @greenlight-spec: 32-knowledge.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PaginationQuerySchema, paginated } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
// Knowledge entry scope (org / integration / app). One entry row per
// (org, scope, integration?, app?, topic). See 32-knowledge.md §Entry model.
export const KNOWLEDGE_SCOPES = ['org', 'integration', 'app'];
export const KnowledgeScopeSchema = z.enum(KNOWLEDGE_SCOPES);
// Visibility — `dashboard_only` rows never appear in MCP responses.
export const KNOWLEDGE_VISIBILITIES = ['agent', 'dashboard_only', 'both'];
export const KnowledgeVisibilitySchema = z.enum(KNOWLEDGE_VISIBILITIES);
export const LAST_EDITOR_KINDS = ['human', 'agent'];
export const LastEditorKindSchema = z.enum(LAST_EDITOR_KINDS);
const isoDateTime = () => z.string().datetime({ offset: true });
const nullable = (s) => s.nullable();
// Editor identity — embedded directly into list/proposal rows so the UI can
// render an attribution chip without a second lookup. Email is what the demo
// world models; a real implementation may also surface display name.
export const KnowledgeEditorSchema = z.object({
    userId: z.string().uuid().nullable(),
    email: z.string().nullable(),
    agentSessionId: z.string().uuid().nullable(),
});
// Summary surface used by the entry list. Body is omitted — the editor loads
// it via `get` on click. Mirrors 28-it-dashboard-ui.md §`/knowledge` "shows".
export const KnowledgeEntrySummarySchema = z.object({
    id: z.string().uuid(),
    scope: KnowledgeScopeSchema,
    integrationId: nullable(z.string().uuid()),
    appId: nullable(z.string().uuid()),
    topic: z.string().min(1),
    title: z.string().min(1),
    visibility: KnowledgeVisibilitySchema,
    tags: z.array(z.string()),
    version: z.number().int().min(1),
    updatedAt: isoDateTime(),
    lastEditor: KnowledgeEditorSchema,
    lastEditorKind: LastEditorKindSchema,
    seededFrom: nullable(z.string()),
});
export const KnowledgeEntrySchema = KnowledgeEntrySummarySchema.extend({
    bodyMd: z.string(),
    createdAt: isoDateTime(),
});
export const KnowledgeHistoryEventSchema = z.object({
    id: z.string().min(1),
    entryId: z.string().uuid(),
    version: z.number().int().min(1),
    action: z.enum(['created', 'updated']),
    actor: KnowledgeEditorSchema,
    actorKind: LastEditorKindSchema,
    diffSummary: z.string(),
    createdAt: isoDateTime(),
});
export const KNOWLEDGE_PROPOSAL_STATUSES = [
    'pending',
    'accepted',
    'rejected',
    'superseded',
];
export const KnowledgeProposalStatusSchema = z.enum(KNOWLEDGE_PROPOSAL_STATUSES);
export const KnowledgeProposalSchema = z.object({
    id: z.string().uuid(),
    entryId: nullable(z.string().uuid()), // null when the proposal creates a new entry
    scope: KnowledgeScopeSchema,
    integrationId: nullable(z.string().uuid()),
    appId: nullable(z.string().uuid()),
    topic: z.string().min(1),
    title: z.string().min(1),
    bodyMd: z.string(),
    rationale: z.string(),
    baseVersion: nullable(z.number().int().min(1)),
    status: KnowledgeProposalStatusSchema,
    acceptedWithEdits: z.boolean(),
    reviewNotes: nullable(z.string()),
    proposedAt: isoDateTime(),
    proposedBy: KnowledgeEditorSchema,
    reviewedAt: nullable(isoDateTime()),
    reviewedBy: nullable(KnowledgeEditorSchema),
});
const EntryIdParams = z.object({ id: z.string().uuid() });
const ProposalIdParams = z.object({ id: z.string().uuid() });
export const EntriesQuerySchema = PaginationQuerySchema.extend({
    scope: KnowledgeScopeSchema.optional(),
    integrationId: z.string().uuid().optional(),
    appId: z.string().uuid().optional(),
});
export const ProposalsQuerySchema = PaginationQuerySchema.extend({
    scope: KnowledgeScopeSchema.optional(),
    status: KnowledgeProposalStatusSchema.optional(),
});
export const CreateKnowledgeEntrySchema = z
    .object({
    scope: KnowledgeScopeSchema,
    integrationId: z.string().uuid().optional(),
    appId: z.string().uuid().optional(),
    topic: z.string().min(1).max(63),
    title: z.string().min(1).max(255),
    bodyMd: z.string().max(100_000),
    visibility: KnowledgeVisibilitySchema.default('both'),
    tags: z.array(z.string()).max(32).default([]),
})
    .superRefine((val, ctx) => {
    if (val.scope === 'integration' && !val.integrationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['integrationId'],
            message: 'integrationId is required when scope is integration',
        });
    }
    if (val.scope === 'app' && !val.appId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['appId'],
            message: 'appId is required when scope is app',
        });
    }
});
export const UpdateKnowledgeEntrySchema = z.object({
    title: z.string().min(1).max(255).optional(),
    bodyMd: z.string().max(100_000).optional(),
    visibility: KnowledgeVisibilitySchema.optional(),
    tags: z.array(z.string()).max(32).optional(),
});
// Accept body — omitted fields fall back to the proposal's values. The server
// distinguishes "accept as proposed" from "accept with edits" by whether the
// supplied field actually *differs* from the live proposal (a client that
// echoes the unchanged body back should not be credited as a human editor).
// See 32-knowledge.md §Proposal flow.
export const AcceptProposalSchema = z.object({
    title: z.string().min(1).max(255).optional(),
    bodyMd: z.string().max(100_000).optional(),
});
export const RejectProposalSchema = z.object({
    reviewNotes: z.string().min(1).max(2000),
});
// Assets — image files attached to an entry (32-knowledge.md §Assets). Every
// asset inherits its parent entry's scope, visibility, and edit permission, so
// none of those appear here.
export const KNOWLEDGE_ASSET_ROLES = [
    'logo-primary',
    'logo-mark',
    'logo-wordmark',
    'icon',
    'favicon',
    'illustration',
    'diagram',
    'other',
];
export const KnowledgeAssetRoleSchema = z.enum(KNOWLEDGE_ASSET_ROLES);
export const KNOWLEDGE_ASSET_THEMES = ['any', 'light', 'dark'];
export const KnowledgeAssetThemeSchema = z.enum(KNOWLEDGE_ASSET_THEMES);
/**
 * Declared content type. Deliberately a plain string, not an enum: the server
 * owns the allowlist so it can answer with the documented
 * `knowledge.asset_type_unsupported` code (docs/32 §Upload) and check the actual
 * magic bytes in the same place. An enum here would reject a bad type earlier
 * with a different code and leave the allowlist defined twice.
 */
export const KnowledgeAssetContentTypeSchema = z.string().min(1).max(128);
/** Slug grammar matches the platform identifier convention (16-api-conventions.md). */
export const KnowledgeAssetSlugSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/, 'slug must be lowercase kebab-case');
export const KnowledgeAssetSchema = z.object({
    id: z.string().uuid(),
    entryId: z.string().uuid(),
    slug: z.string(),
    role: KnowledgeAssetRoleSchema,
    theme: KnowledgeAssetThemeSchema,
    filename: z.string(),
    contentType: z.string(),
    byteSize: z.number().int().nonnegative(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    checksumSha256: z.string(),
    description: z.string(),
    updatedAt: isoDateTime(),
    /**
     * Short-TTL signed URL for rendering the thumbnail in the dashboard. Present
     * only on the dashboard list; the agent's download URL is minted separately by
     * `getAssetDownloadUrl`, which audits the mint. Rendering a grid of thumbnails
     * is not "someone took a copy of the logo", so it deliberately does not audit —
     * one event per thumbnail per page view would bury the events that matter.
     */
    previewUrl: z.string().nullable().default(null),
});
// 2 MiB of raw bytes, base64-encoded (~33% overhead), plus headroom. The server
// re-checks the decoded length — this bound only keeps an absurd payload from
// being buffered and decoded at all.
const MAX_BASE64_LEN = Math.ceil((2 * 1024 * 1024 * 4) / 3) + 1024;
export const CreateKnowledgeAssetSchema = z.object({
    slug: KnowledgeAssetSlugSchema,
    role: KnowledgeAssetRoleSchema.default('other'),
    theme: KnowledgeAssetThemeSchema.default('any'),
    filename: z.string().min(1).max(255),
    contentType: KnowledgeAssetContentTypeSchema,
    // Required: it is the dashboard's alt text and the agent's cue for which file
    // to reach for, so an asset without one is not usable by either reader.
    description: z.string().min(1).max(2000),
    bytesBase64: z.string().min(1).max(MAX_BASE64_LEN),
});
/** Metadata-only edit. Replacing bytes re-uses the create route on the same slug. */
export const UpdateKnowledgeAssetSchema = z.object({
    role: KnowledgeAssetRoleSchema.optional(),
    theme: KnowledgeAssetThemeSchema.optional(),
    description: z.string().min(1).max(2000).optional(),
    filename: z.string().min(1).max(255).optional(),
});
const AssetIdParams = z.object({ id: z.string().uuid() });
export const knowledgeContract = c.router({
    listEntries: {
        method: 'GET',
        path: '/api/knowledge/entries',
        query: EntriesQuerySchema,
        responses: {
            200: paginated(KnowledgeEntrySummarySchema),
            ...errorResponses,
        },
        summary: 'List Knowledge entries scoped to org / integration / app.',
    },
    getEntry: {
        method: 'GET',
        path: '/api/knowledge/entries/:id',
        pathParams: EntryIdParams,
        responses: {
            200: KnowledgeEntrySchema,
            ...errorResponses,
        },
        summary: 'Fetch a single Knowledge entry (body + metadata).',
    },
    createEntry: {
        method: 'POST',
        path: '/api/knowledge/entries',
        body: CreateKnowledgeEntrySchema,
        responses: {
            201: KnowledgeEntrySchema,
            ...errorResponses,
        },
        summary: 'Create a new Knowledge entry.',
    },
    updateEntry: {
        method: 'PATCH',
        path: '/api/knowledge/entries/:id',
        pathParams: EntryIdParams,
        body: UpdateKnowledgeEntrySchema,
        responses: {
            200: KnowledgeEntrySchema,
            ...errorResponses,
        },
        summary: 'Update a Knowledge entry (increments version, writes audit diff).',
    },
    deleteEntry: {
        method: 'DELETE',
        path: '/api/knowledge/entries/:id',
        pathParams: EntryIdParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            ...errorResponses,
        },
        summary: 'Delete a Knowledge entry. Audit log retains the deletion event.',
    },
    listHistory: {
        method: 'GET',
        path: '/api/knowledge/entries/:id/history',
        pathParams: EntryIdParams,
        query: PaginationQuerySchema,
        responses: {
            200: paginated(KnowledgeHistoryEventSchema),
            ...errorResponses,
        },
        summary: 'List edit history for a Knowledge entry (most recent first).',
    },
    listAssets: {
        method: 'GET',
        path: '/api/knowledge/entries/:id/assets',
        pathParams: EntryIdParams,
        responses: {
            200: z.object({ items: z.array(KnowledgeAssetSchema) }),
            ...errorResponses,
        },
        summary: 'List the image files attached to a Knowledge entry.',
    },
    createAsset: {
        method: 'POST',
        path: '/api/knowledge/entries/:id/assets',
        pathParams: EntryIdParams,
        body: CreateKnowledgeAssetSchema,
        responses: {
            201: KnowledgeAssetSchema,
            ...errorResponses,
        },
        summary: 'Attach an image to a Knowledge entry, or replace the bytes on an existing slug.',
    },
    updateAsset: {
        method: 'PATCH',
        path: '/api/knowledge/assets/:id',
        pathParams: AssetIdParams,
        body: UpdateKnowledgeAssetSchema,
        responses: {
            200: KnowledgeAssetSchema,
            ...errorResponses,
        },
        summary: "Edit an asset's metadata. Does not change the entry's version.",
    },
    deleteAsset: {
        method: 'DELETE',
        path: '/api/knowledge/assets/:id',
        pathParams: AssetIdParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            ...errorResponses,
        },
        summary: 'Delete an asset and the stored bytes behind it.',
    },
    getAssetDownloadUrl: {
        method: 'POST',
        path: '/api/knowledge/assets/:id/download-url',
        pathParams: AssetIdParams,
        body: c.noBody(),
        responses: {
            200: z.object({ downloadUrl: z.string().url(), expiresAt: isoDateTime() }),
            ...errorResponses,
        },
        summary: 'Mint a short-TTL read URL for an asset. Audited as knowledge.asset.read.',
    },
    listProposals: {
        method: 'GET',
        path: '/api/knowledge/proposals',
        query: ProposalsQuerySchema,
        responses: {
            200: paginated(KnowledgeProposalSchema),
            ...errorResponses,
        },
        summary: 'List Knowledge proposals from builder agents.',
    },
    acceptProposal: {
        method: 'POST',
        path: '/api/knowledge/proposals/:id/accept',
        pathParams: ProposalIdParams,
        body: AcceptProposalSchema,
        responses: {
            200: KnowledgeEntrySchema,
            ...errorResponses,
        },
        summary: 'Accept a proposal (optionally with edits); upserts the entry.',
    },
    rejectProposal: {
        method: 'POST',
        path: '/api/knowledge/proposals/:id/reject',
        pathParams: ProposalIdParams,
        body: RejectProposalSchema,
        responses: {
            200: KnowledgeProposalSchema,
            ...errorResponses,
        },
        summary: 'Reject a proposal (review notes required).',
    },
});
//# sourceMappingURL=knowledge.js.map